import React from 'react';
import { Badge, Box, Flex, Group, Paper, Text, ThemeIcon, Title } from '@mantine/core';
import { IconCalendar, IconCheck, IconUsers } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { PerformanceReviewRoundStage } from '../../../api/generated/model/performanceReviewRoundStage';
import { Eyebrow, NumberMono } from './primitives';
import { DeadlineInfo, StageDef, ViewStage } from './types';

const StageDot: React.FC<{ status: 'done' | 'active' | 'todo'; index: number }> = ({
  status,
  index,
}) => {
  if (status === 'done') {
    return (
      <ThemeIcon radius="xl" size={26} color="teal" variant="filled">
        <IconCheck size={14} />
      </ThemeIcon>
    );
  }
  if (status === 'active') {
    return (
      <ThemeIcon
        radius="xl"
        size={26}
        color="teal"
        variant="outline"
        style={{
          boxShadow: '0 0 0 4px var(--mantine-primary-color-light)',
          fontWeight: 600,
          fontSize: 12,
        }}
      >
        {index}
      </ThemeIcon>
    );
  }
  return (
    <ThemeIcon
      radius="xl"
      size={26}
      color="gray"
      variant="default"
      style={{ fontSize: 12, fontWeight: 500 }}
    >
      {index}
    </ThemeIcon>
  );
};

const StageTimeline: React.FC<{
  stages: StageDef[];
  currentKey: PerformanceReviewRoundStage;
}> = ({ stages, currentKey }) => {
  const { t } = useTranslation();
  const currentIdx = Math.max(
    0,
    stages.findIndex((s) => s.key === currentKey),
  );
  return (
    <Flex gap={0} align="stretch" style={{ overflowX: 'auto', scrollbarWidth: 'thin' }}>
      {stages.map((s, i) => {
        const status: 'done' | 'active' | 'todo' =
          i < currentIdx ? 'done' : i === currentIdx ? 'active' : 'todo';
        const isLast = i === stages.length - 1;
        return (
          <Box
            key={s.key}
            style={{ flex: 1, minWidth: 160, display: 'flex', flexDirection: 'column' }}
          >
            <Group gap={10} mb={10} wrap="nowrap" align="center">
              <StageDot status={status} index={i + 1} />
              {!isLast && (
                <Box
                  style={{
                    flex: 1,
                    height: 2,
                    background:
                      status === 'done'
                        ? 'var(--mantine-primary-color-filled)'
                        : status === 'active'
                          ? 'linear-gradient(90deg, var(--mantine-primary-color-filled), var(--mantine-color-default-border))'
                          : 'var(--mantine-color-default-border)',
                    borderRadius: 999,
                  }}
                />
              )}
            </Group>
            <Box pr={isLast ? 0 : 24}>
              <Eyebrow color={status === 'active' ? 'teal' : 'dimmed'}>
                {t('perfReview.home.hero.step', { n: String(i + 1).padStart(2, '0') })}
              </Eyebrow>
              <Text
                size="md"
                fw={600}
                c={status === 'todo' ? 'dimmed' : undefined}
                mt={4}
                mb={4}
                style={{ letterSpacing: '-0.01em' }}
              >
                {s.label}
              </Text>
              <Text size="xs" c="dimmed">
                {s.range}
              </Text>
            </Box>
          </Box>
        );
      })}
    </Flex>
  );
};

const DeadlinePanel: React.FC<{ info: DeadlineInfo; isMyWorkDone?: boolean }> = ({
  info,
  isMyWorkDone,
}) => {
  const { t } = useTranslation();
  // Просрочен раунд, а не человек: свою часть он сдал, и красная плашка про
  // чужие долги только пугает. Тому, кто ещё должен, она остаётся.
  const isOverdue = info.days < 0 && !isMyWorkDone;
  const overdueDays = Math.abs(info.days);
  const tone: 'done' | 'overdue' | 'warn' | 'accent' | 'neutral' = isMyWorkDone
    ? 'done'
    : isOverdue
      ? 'overdue'
      : info.days === 0
        ? 'neutral'
        : info.days <= 5
          ? 'warn'
          : 'accent';
  const bg = {
    done: 'var(--mantine-color-teal-light)',
    overdue: 'var(--mantine-color-red-light)',
    warn: 'var(--mantine-color-orange-light)',
    accent: 'var(--mantine-primary-color-light)',
    neutral: 'var(--mantine-color-default-hover)',
  }[tone];
  const fg = {
    done: 'var(--mantine-color-teal-light-color)',
    overdue: 'var(--mantine-color-red-light-color)',
    warn: 'var(--mantine-color-orange-light-color)',
    accent: 'var(--mantine-primary-color-light-color)',
    neutral: 'var(--mantine-color-text)',
  }[tone];
  return (
    <Box
      style={{
        background: bg,
        color: fg,
        borderRadius: 'var(--mantine-radius-md)',
        padding: '14px 18px',
        flex: '1 1 200px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Eyebrow color="inherit">
        {isMyWorkDone
          ? t('perfReview.home.deadline.yourPartDone')
          : isOverdue
            ? t('perfReview.home.deadline.overdue')
            : info.days > 0
              ? t('perfReview.home.deadline.remaining', { count: info.days })
              : t('perfReview.home.deadline.deadline')}
      </Eyebrow>
      {isMyWorkDone ? (
        <Flex align="center" gap={8}>
          <IconCheck size={22} stroke={2.5} />
          <Text component="span" size="xl" fw={700} style={{ letterSpacing: '-0.01em' }}>
            {t('perfReview.home.deadline.doneValue')}
          </Text>
        </Flex>
      ) : (
        <Flex align="baseline" gap={6}>
          <NumberMono
            style={{
              fontSize: 32,
              lineHeight: 1.1,
              fontWeight: 700,
              letterSpacing: '-0.02em',
            }}
          >
            {isOverdue ? overdueDays : info.days > 0 ? info.days : '—'}
          </NumberMono>
          <Text component="span" size="sm" fw={500}>
            {isOverdue
              ? t('perfReview.home.deadline.day', { count: overdueDays })
              : info.days > 0
                ? t('perfReview.home.deadline.day', { count: info.days })
                : ''}
          </Text>
        </Flex>
      )}
      <Text size="xs" style={{ opacity: 0.85 }}>
        {info.label} · {info.date}
      </Text>
    </Box>
  );
};

interface RoundHeroProps {
  title: string;
  description: string;
  period: { start: string; end: string; year: string };
  participantsCount: number;
  stages: StageDef[];
  stage: ViewStage;
  deadline: DeadlineInfo | null;
  isMyWorkDone?: boolean;
  isMobile?: boolean;
}

export const RoundHero: React.FC<RoundHeroProps> = ({
  title,
  description,
  period,
  participantsCount,
  stages,
  stage,
  deadline,
  isMyWorkDone,
  isMobile,
}) => {
  const { t } = useTranslation();
  const isNoRound = stage === 'no-round';
  const isFinished = stage === 'finished';
  const timelineStage: PerformanceReviewRoundStage =
    isNoRound || stage === 'draft' ? 'collect-essay' : stage;

  return (
    <Paper withBorder radius="lg" p={0} style={{ overflow: 'hidden' }}>
      <Box
        p={isMobile ? 'md' : 'lg'}
        style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
      >
        <Flex align="flex-start" justify="space-between" gap="lg" wrap="wrap">
          {/* Текст тянется сильнее плашки срока; когда обоим тесно, плашка уходит вниз во всю ширину. */}
          <Box style={{ flex: '999 1 320px', minWidth: 0 }}>
            <Group gap={8} mb="sm" wrap="wrap">
              <Badge
                color={isNoRound || isFinished ? 'gray' : 'teal'}
                variant="light"
                radius="xl"
                size="sm"
                leftSection={
                  <Box
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: 'currentColor',
                    }}
                  />
                }
              >
                {isNoRound
                  ? t('perfReview.home.hero.noActiveRound')
                  : isFinished
                    ? t('perfReview.home.hero.finishedRound')
                    : t('perfReview.home.hero.activeRound')}
              </Badge>
              <Badge
                variant="default"
                radius="xl"
                size="sm"
                leftSection={<IconCalendar size={12} />}
              >
                {period.start} — {period.end} {period.year}
              </Badge>
              <Badge variant="default" radius="xl" size="sm" leftSection={<IconUsers size={12} />}>
                {t('perfReview.home.hero.participantsCount', { count: participantsCount })}
              </Badge>
            </Group>
            <Title
              order={1}
              size={isMobile ? 'h3' : 'h2'}
              mb={6}
              style={{ letterSpacing: '-0.01em' }}
            >
              {title}
            </Title>
            <Text c="dimmed" size="sm" lh={1.55} style={{ maxWidth: 640 }}>
              {description}
            </Text>
          </Box>
          {deadline && <DeadlinePanel info={deadline} isMyWorkDone={isMyWorkDone} />}
        </Flex>
      </Box>
      <Box p={isMobile ? 'md' : 'lg'} style={{ background: 'var(--mantine-color-default-hover)' }}>
        <StageTimeline stages={stages} currentKey={timelineStage} />
      </Box>
    </Paper>
  );
};
